import React, { useEffect, useState } from "react";
import AuthLayout from "../../layouts/AuthLayout/AuthLayout";
import * as Yup from "yup";
import { Field, Form, Formik } from "formik";
import Input from "../../components/Input/Input";
import Button from "../../components/Button/Button";
import styles from "./Login.module.scss";
import axiosInstance from "../../utils/utils";
import { useDispatch } from "react-redux";
import { showModal } from "../../redux/modalStore";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail } from "lucide-react";

const RESEND_COOLDOWN_MS = 60 * 1000; //60s

const Login = () => {
  const { t } = useTranslation();

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("login.validation.invalidEmail"))
      .required(t("login.validation.required")),
  });

  const dispatch = useDispatch();
  const location = useLocation();

  // Load persisted state (survives refresh)
  const [emailSent, setEmailSent] = useState(() => {
    const flag = localStorage.getItem("login_email_sent");
    return flag === "true";
  });
  const [lastEmail, setLastEmail] = useState(
    () => localStorage.getItem("login_email") || "",
  );
  const [emailSentAt, setEmailSentAt] = useState(() => {
    const ts = Number(localStorage.getItem("login_sent_at") || 0);
    return Number.isFinite(ts) ? ts : 0;
  });

  // Cooldown countdown (seconds)
  const [cooldownLeft, setCooldownLeft] = useState(0);
  useEffect(() => {
    if (!emailSentAt) return;
    const tick = () => {
      const left = Math.max(
        0,
        Math.ceil((RESEND_COOLDOWN_MS - (Date.now() - emailSentAt)) / 1000),
      );
      setCooldownLeft(left);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [emailSentAt]);

  const canResend = emailSent && !!lastEmail && cooldownLeft === 0;

  const handleSubmit = (values, actions) => {
    const options = {
      method: "POST",
      url: "/auth/session",
      params: {
        email: values.email,
      },
    };

    return axiosInstance
      .request(options)
      .then(() => {
        const now = Date.now();
        setLastEmail(values.email);
        setEmailSent(true);
        setEmailSentAt(now);
        localStorage.setItem("login_email", values.email);
        localStorage.setItem("login_email_sent", "true");
        localStorage.setItem("login_sent_at", String(now));
      })
      .catch((error) => {
        console.error("Login error:", error);
        dispatch(
          showModal({
            name: "error_modal",
          }),
        );
      })
      .finally(() => {
        actions.setSubmitting(false);
      });
  };

  const handleResend = async () => {
    if (!canResend) return;
    try {
      const now = Date.now();
      await axiosInstance.request({
        method: "POST",
        url: "/auth/session",
        params: { email: lastEmail },
      });
      setEmailSentAt(now);
      localStorage.setItem("login_sent_at", String(now));
    } catch (error) {
      console.error("Resend error:", error);
      dispatch(showModal({ name: "error_modal" }));
    }
  };

  const formatSeconds = (s) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const showExpiredBanner = location.state?.expired;

  return (
    <AuthLayout>
      <div className={styles.container}>
        {!emailSent && (
          <>
            <div className={styles.header}>
              <div className={styles.title}>Welcome to Borderly+</div>
              <p className={styles.subtitle}>
                Sign in to access your customs compliance dashboard
              </p>
            </div>
            <Formik
              initialValues={{ email: "" }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, values, isValid }) => (
                <Form className={styles.form}>
                  {showExpiredBanner && (
                    <div className={styles.noticeError}>
                      {t("login.expiredBanner")}
                    </div>
                  )}
                  <div className={styles.fieldGroup}>
                    <label htmlFor="email" className={styles.label}>
                      Email address
                    </label>
                    <Field
                      id="email"
                      name="email"
                      type="email"
                      component={Input}
                      placeholder={t("login.placeholder")}
                      className={styles.input}
                    />
                  </div>
                  <div className={styles.magicInfo}>
                    <span className={styles.magicIcon}>
                      <Mail />
                    </span>
                    <span>
                      No password needed! We&apos;ll send you a secure link to sign
                      in instantly.
                    </span>
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting || !values.email || !isValid}
                    text={isSubmitting ? t("login.sending") : t("login.submit")}
                    className={styles.button}
                    icon={<Mail />}
                  />
                </Form>
              )}
            </Formik>
          </>
        )}
        {emailSent && (
          <div className={styles.emailSent}>
            <div className={styles.emailIconCircle}>
              <Mail />
            </div>
            <div>
              <div className={styles.emailTitle}>
                {t("login.emailSent.title")}
              </div>
              <p className={styles.emailText}>
                {t("login.emailSent.text")}{" "}
                <span className={styles.emailHighlight}>{lastEmail}</span>
              </p>
            </div>
            <div className={styles.stepsBox}>
              <div className={styles.stepsTitle}>
                {t("login.emailSent.stepsTitle")}
              </div>
              <ol className={styles.stepsList}>
                <li>{t("login.emailSent.steps.first")}</li>
                <li>{t("login.emailSent.steps.second")}</li>
                <li>{t("login.emailSent.steps.third")}</li>
              </ol>
            </div>
            <button
              type="button"
              className={styles.changeEmailButton}
              onClick={() => {
                setEmailSent(false);
                localStorage.setItem("login_email_sent", "false");
              }}
            >
              {t("login.useDifferentEmail")}
            </button>
          </div>
        )}
      </div>
    </AuthLayout>
  );
};

export default Login;
